import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Star, Lightning, CheckCircle, Timer, SpeakerHigh } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useLumi, LumiInline } from "./LumiCharacter";
import { LumiReplayButton } from "./LumiReplayButton";
import { useKidsSound } from "@/hooks/useKidsSound";
import { speakLumi, stopLumiAudio, clearLumiReplay } from "@/lib/lumiAudio";
import { loadProgress, saveProgress, clearProgress, shuffle, newProgress } from "@/lib/wordGameProgress";
import wordGamesBank from "@/data/wordGamesBank.json";

// ---------- WORD DATA ----------
// WordGames_ActivityBank_v2_FINAL.json is the ONLY content source: words, prompts, answer
// choices, corrective feedback, success messages and Lumi poses are used exactly as approved.
// Choices keep their stored order (incorrect_feedback is keyed by choice letter A/B/C).
const ACTIVITIES = wordGamesBank.activities;
const BY_ID = Object.fromEntries(ACTIVITIES.map((a) => [a.activity_id, a]));

const MODES = [
  { key: "Match the Meaning", emoji: "📖", color: "from-[#e3f0ff] to-[#f4f8ff]", testid: "match-meaning" },
  { key: "Find the Synonym", emoji: "🤝", color: "from-[#e6f7f5] to-[#f3fbfa]", testid: "find-synonym" },
  { key: "Find the Antonym", emoji: "🔄", color: "from-[#ffe9f1] to-[#fff5f9]", testid: "find-antonym" },
  { key: "Choose the Stronger Word", emoji: "💪", color: "from-[#fff3dc] to-[#fff9ee]", testid: "stronger-word" },
  { key: "Context Clue Detective", emoji: "🔍", color: "from-[#efe9fb] to-[#f7f4fd]", testid: "context-clue" },
];

const POINTS_BASE = {
  "Level 1 (Easy)": 10,
  "Level 2 (Medium)": 20,
  "Level 3 (Challenge)": 30,
};
const SPEED_ROUND_SECONDS = 60;
const LETTERS = ["A", "B", "C"];
const PANEL = "bg-white rounded-3xl border border-[#e6ddfa] shadow-[0_8px_30px_rgba(115,88,179,0.10)]";
const PRIMARY_BTN = "bg-[#6a3fc7] hover:bg-[#5a33ad] text-white font-nunito font-extrabold rounded-2xl border-0 focus-visible:ring-4 focus-visible:ring-[#b79cf0]";
const OUTLINE_BTN = "font-nunito font-bold rounded-full border border-[#d9ccf5] bg-white text-[#4a2e8a] hover:bg-[#f3eefc]";

const WordGame = ({ player, onBack, onUpdateScore }) => {
  const lumi = useLumi();
  const [soundOn] = useKidsSound();
  const playerId = player?.id || null;
  const [mode, setMode] = useState(null); // one of MODES[].key | 'speed'

  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null); // set only once the correct answer is chosen
  const [misses, setMisses] = useState([]); // choices already tried on this question (retries are unlimited)
  const [gameOver, setGameOver] = useState(false);
  const [resumed, setResumed] = useState(false);
  const [, setTick] = useState(0); // re-read saved progress for the mode cards

  // Speed mode total timer (optional challenge). The five skill modes are untimed.
  const [speedTimeLeft, setSpeedTimeLeft] = useState(SPEED_ROUND_SECONDS);
  const [timerActive, setTimerActive] = useState(false);
  const speedTimer = useRef(null);

  // On phones, show Lumi inline during a round so she never covers answer buttons
  const inRound = mode !== null && !gameOver;
  useEffect(() => {
    lumi.setInline(inRound);
    return () => lumi.setInline(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inRound]);

  // Activity introduction (Waving)
  useEffect(() => {
    lumi.wave("Let's play with words! Pick a game to begin.", 3000);
    clearLumiReplay();
    return () => { lumi.idle(); stopLumiAudio(); clearTimeout(speedTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Directions (Speaking) when a round starts
  useEffect(() => {
    if (mode && !gameOver && questions[0]) {
      const line = mode === "speed"
        ? "Answer as many as you like — take a breath, you've got this!"
        : resumed ? "Welcome back! Let's pick up where you left off." : questions[currentQuestion]?.directions;
      lumi.say(line, "speaking", 3500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // ---------- TIMER (Speed Round only) ----------
  useEffect(() => {
    if (mode === "speed" && timerActive && speedTimeLeft > 0 && !gameOver) {
      const t = setTimeout(() => setSpeedTimeLeft((s) => s - 1), 1000);
      return () => clearTimeout(t);
    }
    if (mode === "speed" && speedTimeLeft <= 0 && !gameOver) {
      endGame();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speedTimeLeft, timerActive, mode, gameOver]);

  // ---------- PROGRESS (per child, per mode) ----------
  const persist = (gameMode, order, index, s, st, best) => {
    if (gameMode === "speed") return;
    saveProgress(playerId, gameMode, { order: order.map((q) => q.activity_id), index, score: s, streak: st, bestStreak: best });
  };

  const startGame = (gameMode) => {
    stopLumiAudio();
    clearLumiReplay();
    let order;
    let saved = null;
    if (gameMode === "speed") {
      order = shuffle(ACTIVITIES);
    } else {
      saved = loadProgress(playerId, gameMode);
      const p = saved || newProgress(ACTIVITIES.filter((a) => a.game_mode === gameMode).map((a) => a.activity_id));
      order = p.order.map((id) => BY_ID[id]).filter(Boolean);
      if (!saved) saveProgress(playerId, gameMode, p);
      saved = saved ? p : null;
    }
    setMode(gameMode);
    setQuestions(order);
    setCurrentQuestion(saved ? saved.index : 0);
    setScore(saved ? saved.score : 0);
    setStreak(saved ? saved.streak : 0);
    setBestStreak(saved ? saved.bestStreak : 0);
    setResumed(!!saved);
    setGameOver(false);
    setSelectedAnswer(null);
    setMisses([]);

    if (gameMode === "speed") {
      setSpeedTimeLeft(SPEED_ROUND_SECONDS);
      setTimerActive(true);
    } else {
      setTimerActive(false);
    }
  };

  const advanceQuestion = () => {
    lumi.idle(); // success message and Celebrating pose end together, exactly when the next question begins
    stopLumiAudio();
    clearLumiReplay();
    setSelectedAnswer(null);
    setMisses([]);

    if (mode === "speed") {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        // Out of questions — refill (rare in 60s)
        setQuestions((prev) => [...prev, ...shuffle(ACTIVITIES)]);
        setCurrentQuestion(currentQuestion + 1);
      }
    } else if (currentQuestion < questions.length - 1) {
      const next = currentQuestion + 1;
      setCurrentQuestion(next);
      persist(mode, questions, next, score, streak, bestStreak);
      lumi.say(questions[next].directions, "speaking", 3500);
    } else {
      endGame();
    }
  };

  const handleAnswer = (answer, index) => {
    if (selectedAnswer !== null || misses.includes(answer)) return;
    const q = questions[currentQuestion];

    if (answer !== q.correct_answer) {
      // Gentle, unlimited retries: no points removed. Lumi shows the approved feedback for this choice
      // in the try_again pose and stays there for as long as the feedback is visible.
      const feedback = q.incorrect_feedback[LETTERS[index]];
      setMisses((m) => [...m, answer]);
      setStreak(0);
      lumi.tryAgain(feedback, 0, true);
      speakLumi(feedback);
      return;
    }

    setSelectedAnswer(answer);
    const base = POINTS_BASE[q.difficulty_level] || 10;
    const pts = mode === "speed" ? 15 + (streak >= 3 ? 5 : 0) : base + (streak >= 3 ? 5 : 0);
    const newScore = score + pts;
    const ns = streak + 1;
    const best = Math.max(bestStreak, ns);
    setScore(newScore);
    setStreak(ns);
    setBestStreak(best);
    // The finished question is saved right away, so a child who leaves now resumes on the next one.
    persist(mode, questions, currentQuestion + 1, newScore, ns, best);
    // Celebrating Lumi on EVERY correct answer (all modes), with the approved success message,
    // held until the next question begins (see advanceQuestion).
    lumi.celebrate(q.success_feedback, 0);
    speakLumi(q.success_feedback);

    if (mode === "speed") speedTimer.current = setTimeout(advanceQuestion, 1500);
  };

  const endGame = () => {
    setTimerActive(false);
    setGameOver(true);
    if (mode !== "speed") clearProgress(playerId, mode); // the whole set is done → next time it reshuffles

    let badge = null;
    if (player && onUpdateScore) {
      if (!player.badges?.includes("first_game")) badge = "first_game";
      else if (score >= 250 && !player.badges?.includes("word_master")) badge = "word_master";
      else if (bestStreak >= 5 && !player.badges?.includes("streak_5")) badge = "streak_5";
      else if (mode === "speed" && score >= 200 && !player.badges?.includes("speed_demon")) badge = "speed_demon";
      onUpdateScore(score, badge);
    }
    // Round complete → celebrate; a new badge gets a brief Flying moment first
    if (badge) lumi.say("You earned a new badge!", "flying", 2200);
    else lumi.celebrate("Round complete! Wonderful work!", 3000);
  };

  const exitToModePicker = () => {
    lumi.idle();
    stopLumiAudio();
    clearLumiReplay();
    clearTimeout(speedTimer.current);
    setMode(null);
    setGameOver(false);
    setTimerActive(false);
    setTick((t) => t + 1);
  };

  // ---------- RENDER ----------
  // Mode picker (top level)
  if (!mode) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Button variant="outline" onClick={onBack} className={`mb-6 ${OUTLINE_BTN}`} data-testid="wordgame-back-btn">
          <ArrowLeft size={20} weight="bold" className="mr-2" />
          Back to Kid's Corner
        </Button>

        <div className="text-center mb-8">
          <h1 className="font-nunito font-extrabold text-4xl text-[#4a2e8a] mb-3">🎮 Word Games</h1>
          <p className="font-nunito text-lg text-[#5b5572]">Pick a game. No timer, take your time!</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {MODES.map((m) => {
            const saved = loadProgress(playerId, m.key);
            return (
              <ModeCard
                key={m.key}
                emoji={m.emoji}
                title={m.key}
                desc={wordGamesBank.metadata.mode_descriptions[m.key]}
                color={m.color}
                progress={saved ? `Continue · ${saved.index} of ${saved.order.length} done` : null}
                onClick={() => startGame(m.key)}
                testid={`wordgame-mode-${m.testid}`}
              />
            );
          })}
          <ModeCard
            emoji="⚡"
            title="Speed Round (optional)"
            desc="An extra challenge for kids who enjoy it — 60 seconds, as many as you like!"
            color="from-[#ffe9d6] to-[#fff4ea]"
            onClick={() => startGame("speed")}
            testid="wordgame-mode-speed"
          />
        </div>
      </motion.div>
    );
  }

  // Game over
  if (gameOver) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-lg mx-auto">
        <div className={`${PANEL} p-8 text-center`}>
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="font-nunito font-extrabold text-3xl text-[#4a2e8a] mb-4">
            {mode === "speed" ? "Time's Up!" : "Set Complete!"}
          </h1>
          <div className="bg-[#fff6e0] border border-[#f7dca0] rounded-2xl p-6 mb-6">
            <p className="font-nunito text-[#5b5572] mb-2">Your Score</p>
            <p className="font-nunito font-extrabold text-5xl text-[#4a2e8a]" data-testid="wordgame-final-score">{score}</p>
          </div>
          <div className="flex justify-center gap-6 mb-6 flex-wrap">
            <div className="text-center">
              <p className="font-nunito text-sm text-[#5b5572]">Mode</p>
              <p className="font-nunito font-bold text-[#4a2e8a]">{mode === "speed" ? "Speed Round" : mode}</p>
            </div>
            <div className="text-center">
              <p className="font-nunito text-sm text-[#5b5572]">Best Streak</p>
              <p className="font-nunito font-bold text-[#4a2e8a]">{bestStreak} 🔥</p>
            </div>
          </div>
          <div className="space-y-3">
            <Button onClick={() => startGame(mode)} className={`w-full ${PRIMARY_BTN} text-lg py-6`} data-testid="wordgame-play-again-btn">
              {mode === "speed" ? "Play Again" : "Play Again (new order)"}
            </Button>
            <Button variant="outline" onClick={exitToModePicker} className={`w-full ${OUTLINE_BTN} rounded-2xl`}>
              Change Mode
            </Button>
            <Button variant="outline" onClick={onBack} className={`w-full ${OUTLINE_BTN} rounded-2xl`}>
              Back to Kid's Corner
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  const question = questions[currentQuestion];
  if (!question) return null;

  const usingSpeedTimer = mode === "speed";
  const timeWarn = speedTimeLeft <= 10;
  const answered = selectedAnswer !== null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <Button variant="outline" onClick={exitToModePicker} className={OUTLINE_BTN} data-testid="wordgame-quit-btn">
          <ArrowLeft size={20} weight="bold" className="mr-2" />
          Quit
        </Button>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-[#fff6e0] px-4 py-2 rounded-full border border-[#f7dca0]">
            <Star size={20} weight="fill" className="text-[#e9a400]" />
            <span className="font-nunito font-extrabold text-[#4a2e8a]" data-testid="wordgame-score">{score}</span>
          </div>
          {streak >= 3 && (
            <div className="flex items-center gap-1 bg-[#ffe9d6] px-3 py-2 rounded-full border border-[#f8c9a4]">
              <Lightning size={20} weight="fill" className="text-[#e8740f]" />
              <span className="font-nunito font-extrabold text-[#4a2e8a]">{streak}🔥</span>
            </div>
          )}
        </div>
      </div>

      {/* Progress / Timer */}
      {usingSpeedTimer ? (
        <div className="mb-6">
          <div className="flex justify-between text-xs font-nunito font-bold text-[#5b5572] mb-1">
            <span>⚡ Speed Round</span>
            <span>{`Q${currentQuestion + 1}`}</span>
          </div>
          <div className="bg-[#efe9fb] rounded-full h-3 overflow-hidden">
            <div
              className={`h-full transition-all duration-1000 ${timeWarn ? "bg-[#e8740f]" : "bg-[#ff8518]"}`}
              style={{ width: `${(speedTimeLeft / SPEED_ROUND_SECONDS) * 100}%` }}
            />
          </div>
        </div>
      ) : (
        <div className="bg-[#efe9fb] rounded-full h-3 mb-6 overflow-hidden" data-testid="wordgame-progress">
          <div
            className="bg-[#6a3fc7] h-full transition-all duration-300"
            style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
          />
        </div>
      )}

      {usingSpeedTimer && (
        <div className="flex justify-center mb-6">
          <div
            className={`flex items-center gap-2 px-6 py-3 rounded-full border ${
              timeWarn ? "bg-[#fff1e6] border-[#f8c9a4]" : "bg-white border-[#e6ddfa]"
            }`}
            data-testid="wordgame-timer"
          >
            <Timer size={24} weight="fill" className={timeWarn ? "text-[#e8740f]" : "text-[#4a2e8a]"} />
            <span className={`font-nunito font-extrabold text-2xl ${timeWarn ? "text-[#e8740f]" : "text-[#4a2e8a]"}`}>
              {speedTimeLeft}
            </span>
          </div>
        </div>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={question.activity_id + currentQuestion}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          className={`${PANEL} p-6 sm:p-8`}
        >
          <p className="font-nunito text-sm text-[#5b5572] text-center mb-2" data-testid="wordgame-counter">
            {usingSpeedTimer ? question.game_mode : `${question.game_mode} · Question ${currentQuestion + 1} of ${questions.length}`}
          </p>
          <h2 className="font-nunito font-extrabold text-2xl sm:text-3xl text-center text-[#4a2e8a] mb-2" data-testid="wordgame-prompt">
            {question.prompt}
          </h2>
          <p className="font-nunito text-center text-[#5b5572] mb-4">{question.directions}</p>

          <div className="flex items-center justify-center gap-2 flex-wrap mb-5" data-testid="wordgame-audio-controls">
            {soundOn && (
              <button
                type="button"
                onClick={() => speakLumi(question.read_aloud_text || question.prompt)}
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#efe9fb] border border-[#d9ccf5] font-nunito font-bold text-sm text-[#4a2e8a] hover:bg-[#e3d9f8] transition-colors focus-visible:ring-4 focus-visible:ring-[#b79cf0]"
                aria-label="Listen to the question and answer choices"
                data-testid="wordgame-listen-btn"
              >
                <SpeakerHigh size={18} weight="fill" /> Listen
              </button>
            )}
            <LumiReplayButton testid="wordgame-replay-btn" />
          </div>
          <LumiInline className="mb-5" />

          <div className="grid gap-3">
            {question.answer_choices.map((option, i) => {
              const missed = misses.includes(option);
              let btnClass = "bg-white border-[#e6ddfa] hover:bg-[#f7f4fd] hover:border-[#c9b8f0]";
              if (missed) btnClass = "bg-[#f6f4fa] border-[#ebe6f5] text-[#9a93ad]";
              if (answered && option === question.correct_answer) btnClass = "bg-[#e6f7f5] border-[#7fd1c3]";
              return (
                <motion.button
                  key={i}
                  whileHover={!answered && !missed ? { scale: 1.02 } : {}}
                  whileTap={!answered && !missed ? { scale: 0.98 } : {}}
                  disabled={answered || missed}
                  onClick={() => handleAnswer(option, i)}
                  className={`w-full p-4 rounded-2xl border-2 text-left font-nunito font-bold text-[#2f2a45] transition-colors ${btnClass}`}
                  data-testid={`wordgame-option-${i}`}
                  data-missed={missed || undefined}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 bg-[#efe9fb] text-[#4a2e8a] rounded-full flex items-center justify-center font-nunito font-extrabold shrink-0">
                      {LETTERS[i]}
                    </span>
                    <span>{option}</span>
                    {answered && option === question.correct_answer && (
                      <CheckCircle size={24} weight="fill" className="text-[#2fb59f] ml-auto" />
                    )}
                    {missed && !answered && (
                      <span className="ml-auto text-xs font-nunito font-bold text-[#9a93ad]">tried</span>
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Lumi (bubble on desktop, inline on phones) is the single place feedback is shown. */}
          {answered && mode !== "speed" && (
            <Button onClick={advanceQuestion} className={`w-full mt-6 ${PRIMARY_BTN} text-lg py-6`} data-testid="wordgame-next-btn">
              {currentQuestion < questions.length - 1 ? "Next Question" : "Finish"}
            </Button>
          )}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};

// Each game mode is a real button: reachable with Tab, started with Enter or Space, visible focus ring.
const ModeCard = ({ emoji, title, desc, color, progress, onClick, testid }) => (
  <motion.button
    type="button"
    whileHover={{ scale: 1.03, y: -3 }}
    whileTap={{ scale: 0.97 }}
    onClick={onClick}
    data-testid={testid}
    aria-label={progress ? `${title}. ${progress}` : title}
    className={`relative block w-full text-center bg-gradient-to-br ${color} ${PANEL} p-7 cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-[#6a3fc7] focus-visible:ring-offset-2`}
  >
    {progress && (
      <span className="absolute top-4 right-4 bg-[#6a3fc7] text-white font-nunito font-bold text-xs rounded-full px-3 py-1" data-testid={`${testid}-progress`}>
        {progress}
      </span>
    )}
    <span className="block text-6xl text-center mb-3" aria-hidden="true">{emoji}</span>
    <span className="block font-nunito font-extrabold text-2xl text-[#4a2e8a] text-center mb-2">{title}</span>
    <span className="block font-nunito text-center text-[#5b5572]">{desc}</span>
  </motion.button>
);

export default WordGame;
